/**
 * Money is an integer number of paise everywhere — database, API payload, and
 * business logic. Rupees exist only at the display layer.
 * GBV-DEV-2026-001 Section 5: "Money is never a float".
 */
export const PAISE_PER_RUPEE = 100;
/** Convert a rupee amount entered by a person into storable paise. */
export function rupeesToPaise(rupees) {
    if (!Number.isFinite(rupees))
        throw new TypeError('rupeesToPaise needs a finite number.');
    // Round rather than truncate: 12.005 → 1201 paise, not 1200.
    return Math.round(rupees * PAISE_PER_RUPEE);
}
/** Convert stored paise back to a rupee number. Display only — never re-store this. */
export function paiseToRupees(paise) {
    return paise / PAISE_PER_RUPEE;
}
/**
 * Format paise as Indian currency, using the lakh/crore grouping that
 * `en-IN` gives us (₹12,34,567.00 rather than ₹1,234,567.00).
 */
export function formatINR(paise, opts = {}) {
    const { decimals = true, symbol = true } = opts;
    const value = paiseToRupees(paise);
    const formatted = new Intl.NumberFormat('en-IN', {
        style: symbol ? 'currency' : 'decimal',
        currency: 'INR',
        minimumFractionDigits: decimals ? 2 : 0,
        maximumFractionDigits: decimals ? 2 : 0,
    }).format(value);
    return formatted;
}
/**
 * Compact form for dashboard tiles: ₹1.2L, ₹16.5L, ₹2.4Cr.
 * Indian readers expect lakh and crore, not K and M.
 */
export function formatINRCompact(paise) {
    const rupees = paiseToRupees(paise);
    const abs = Math.abs(rupees);
    const sign = rupees < 0 ? '-' : '';
    if (abs >= 1_00_00_000)
        return `${sign}₹${trim(abs / 1_00_00_000)}Cr`;
    if (abs >= 1_00_000)
        return `${sign}₹${trim(abs / 1_00_000)}L`;
    if (abs >= 1_000)
        return `${sign}₹${trim(abs / 1_000)}K`;
    return `${sign}₹${Math.round(abs)}`;
}
function trim(n) {
    return n.toFixed(1).replace(/\.0$/, '');
}
/** Sum paise safely — guards against a float sneaking in from a JSON payload. */
export function sumPaise(values) {
    return values.reduce((total, v) => {
        if (!Number.isInteger(v)) {
            throw new TypeError(`Expected integer paise, received ${v}. Money is never a float.`);
        }
        return total + v;
    }, 0);
}
/**
 * Split a paise amount into n parts without losing or inventing a paisa.
 * The remainder is distributed one paisa at a time across the earlier parts.
 */
export function splitPaise(total, parts) {
    if (parts < 1)
        throw new RangeError('splitPaise needs at least one part.');
    const base = Math.floor(total / parts);
    const remainder = total - base * parts;
    return Array.from({ length: parts }, (_, i) => base + (i < remainder ? 1 : 0));
}
//# sourceMappingURL=currency.js.map